import TransactionModal from "./transactionModal.js";
import { $_GET } from "../globalFunctions.js";

export default class LoanModal extends TransactionModal {
  constructor(modalId, companyId) {
    super(modalId, companyId);

    this.$date = this.$form.querySelector('[name="date"]');
  }

  setTransactionData(transaction) {
    this.transaction = transaction;
    const loan = transaction.loan;
    this.earnedInterest = 0;

    this.setFloat("data-amount", loan.amount);
    this.setFloat("data-interest", loan.interest_percent);
    this.setFloat("data-discount", loan.discount);
    this.setFloat("data-cap", loan.valuation_cap);

    if (loan.interest_percent > 0) {
      this.setEarnedInterest(this.calculateEarnedInterest(loan));
    }

    this.setFloat(
      "data-floor",
      loan.valuation_floor ? loan.valuation_floor : 0
    );

    this.setAmountToConvert(
      this.calculateAmountToConvert(
        loan.amount,
        this.earnedInterest,
        this.getFloat("retention")
      )
    );
  }

  calculateEarnedInterest(loan) {
    let earnedInterest = 0;
    if (loan.interest_percent > 0) {
      const passedDays = this.dateDiffInDays(loan.interest_start_date);
      const dailyInterest = loan.interest_percent / loan.interest_days;
      earnedInterest = (dailyInterest / 100) * passedDays * loan.amount;
    }

    // probably wrong date
    if (earnedInterest < 0) {
      earnedInterest = 0;
    }

    return earnedInterest;
  }

  setEarnedInterest(earnedInterest) {
    this.earnedInterest = earnedInterest;
    this.setFloat("data-earned-interest", this.earnedInterest);
    this.setAmountToConvert(
      this.calculateAmountToConvert(
        this.transaction.loan.amount,
        earnedInterest,
        this.getFloat("retention")
      )
    );
  }

  dateDiffInDays(startDate) {
    const _MS_PER_DAY = 1000 * 60 * 60 * 24;
    const date = this.$date.valueAsDate;
    if (date === null) {
      // they set an invalid date
      return 1;
    }
    const selectedDate = date.getTime();

    return Math.floor((selectedDate - Date.parse(startDate)) / _MS_PER_DAY);
  }

  calculateAmountToConvert(loanAmount, earnedInterest, retention) {
    let total = earnedInterest + loanAmount;

    if (retention > 0) {
      total -= (retention * earnedInterest) / 100;
    }

    return total;
  }

  setAmountToConvert(total) {
    this.toConvert = total;
    this.setFloat("data-to-convert", total);
  }

  getPublicValuation(date) {
    return new Promise((resolve, reject) => {
      peque
        .api(
          "valuations/premoney",
          {
            company: this.companyId,
            date: date,
            simulation: $_GET("simulation"),
          },
          "get"
        )
        .then((response) => {
          this.valuation = response.result;
          try {
            this.setFloat("premoney", this.valuation.valuation);
          } catch (e) {}

          resolve(response.result);
        });
    });
  }

  getPPSElement() {
    return this.$modal._element.querySelector("[data-pps]");
  }

  getIssuedSharesElement() {
    return this.$modal._element.querySelector("[data-issued-shares]");
  }

  buildSummaryPhrase(
    loan,
    appliedCap,
    toReturn,
    shares,
    appliedValuation,
    pps
  ) {
    let phrase = "";
    phrase += trans("convertible_conversion_summary_phrase", {
      convertibleAmount: peque.formatCurrency(loan.amount, true, 0, false, 2),
      shares: peque.formatNumber(shares, false, false, 0, 0),
      pps: pps,
      limit: appliedCap ? trans(appliedCap) : trans("valuation"),
      limitValuation:
        appliedCap === "discount"
          ? `${loan.discount}%`
          : peque.formatCurrency(appliedValuation, false, 2, false, 2),
    });

    if (toReturn > 0) {
      phrase +=
        " " +
        trans(
          "and_also_paying_back_payoutamount_companycurrency_to_the_stakeholder",
          {
            payoutAmount: peque.formatCurrency(toReturn, false, 2, false, 2),
          }
        );
    }
    return phrase;
  }

  calculateShares(valuation, loan, premoneyValuation, investment, type) {
    if (typeof valuation === "undefined") {
      return;
    }
    let appliedValuation = JSON.parse(JSON.stringify(premoneyValuation)); // clone var without ref
    let appliedCap = "";
    let capOrFloorApplied = false;

    if (loan.valuation_cap > 0 && appliedValuation >= loan.valuation_cap) {
      appliedValuation = loan.valuation_cap;
      capOrFloorApplied = true;
      appliedCap = "cap";
    } else if (
      loan.valuation_floor > 0 &&
      appliedValuation < loan.valuation_floor
    ) {
      appliedValuation = loan.valuation_floor;
      capOrFloorApplied = true;
      appliedCap = "floor";
    }

    const amount = investment;

    let sharePrice = appliedValuation / valuation.shares;

    if (type === "postmoney" && appliedValuation === loan.valuation_cap) {
      if (valuation.postMoneyShares) {
        sharePrice =
          loan.valuation_cap /
          (valuation.existingSharesAndPreMoneyShares +
            valuation.postMoneyShares);
      } else {
        const expectedPercentage = (investment / appliedValuation) * 100;
        const sharesToIssue = Math.round(
          (expectedPercentage / (100 - expectedPercentage)) * valuation.shares
        );
        const existingShares = valuation.existingSharesAndPreMoneyShares
          ? valuation.existingSharesAndPreMoneyShares
          : valuation.shares;
        sharePrice = loan.valuation_cap / (existingShares + sharesToIssue);
      }
    }

    if (loan.discount > 0) {
      if (!capOrFloorApplied) {
        sharePrice -= (sharePrice * loan.discount) / 100;
        appliedCap = "discount";
      } else if (appliedCap === "cap") {
        let discountPrice = premoneyValuation / valuation.shares;
        discountPrice -= (discountPrice * loan.discount) / 100;

        if (discountPrice < sharePrice) {
          sharePrice = discountPrice;
          capOrFloorApplied = false;
          appliedCap = "discount";
        }
      }
    }

    let shares = Math.round(amount / sharePrice);

    let diff = amount / sharePrice - shares;
    let toReturn = diff * sharePrice;

    if (isNaN(toReturn) || toReturn < 0) {
      toReturn = 0;
    }

    return {
      appliedValuation,
      sharePrice,
      shares,
      appliedCap,
      toReturn,
    };
  }
}
